import { useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Col, Dropdown, DropdownButton, Row } from "react-bootstrap";
import useAlert from "../../components/CustomAlert";
import { useAuth } from "../../providers/authprovider";
import ProtocolsTable from "./ProtocolsTable";
import SearchBuildingBar from "../../components/SearchBuildingBar";
import FolderGrid from "../../components/FolderGrid";
import { usePrivilege } from "../../providers/privilegeprovider";
import Loading from "../../components/LoadingInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import CreateProtocol from "./CreateProtocol";
import { useDataProvider } from "../../providers/offline_data_provider";
import OfflineProtocolQueue from "./OfflineProtocolQueue";
import { PROTOCOL_TYPES } from "./protocolTypes";
import LastPageModal from "./LastPageModal";

function NewProtocolDropdown({ className }) {
  const navigate = useNavigate();

  return (
    <DropdownButton
      className={className}
      title="Nowy protokół"
      id="new-protocol-dropdown"
    >
      {PROTOCOL_TYPES.map((type) => (
        <Dropdown.Item
          key={type.value}
          onClick={() => navigate(`/protocols/create/type/${type.value}`)}
        >
          {type.label}
        </Dropdown.Item>
      ))}
    </DropdownButton>
  );
}


export default function Protocols(props) {
  const navigate = useNavigate();
  const { getPrivilege } = usePrivilege();
  const [buildingFilter, setBuildingFilter] = useState(undefined);
  const [showLastPage, setShowLastPage] = useState(false);
  const privilege = getPrivilege("protocols");
  const { loading, error, buildingFolders, setBuildingFolders } = useDataProvider();

  const folders = useMemo(() => {
    if (!buildingFilter) return buildingFolders;

    return buildingFolders.filter((bf) => bf.building_id === buildingFilter.id);
  }, [buildingFolders, buildingFilter]);

  const handleSetBuildingFilter = (building) => {
    setBuildingFilter(building);
  };

  const openFolder = (folder) => {
    navigate(`./${folder.building_id}/`);
  };

  const handleSave = (protocol, add) => {
    if (!add) return;

    if (!protocol.building_id) return;

    let found = false;

    setBuildingFolders((oldFolders) => {
      oldFolders.forEach((folder) => {
        if (folder.building_id === protocol.building_id) {
          folder.files++;
          found = true;
        }
      });

      if (!found)
        oldFolders.push({
          building_id: protocol.building_id,
          name: protocol.building.building_name,
          files: 1,
        });

      return [...oldFolders];
    });
  };

  const onRemove = (protocol) => {
    setBuildingFolders((oldFolders) => {
      oldFolders.forEach((folder) => {
        if (folder.building_id === protocol.building_id) folder.files--;
      });

      return [...oldFolders];
    });
  };

  const handleEditOfflineProtocol = (id) => {
    navigate(`./offline/${id}`);
  };

  if (loading || error)
    return (
      <>
        {error && (
          <Alert variant={"danger"} className={"m-3"}>
            <Alert.Heading>Wystąpił błąd</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}

        <Loading show={loading} />
      </>
    );

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <div className={"container"}>
              <Row>
                <Col className={"p-3"}>
                  <SearchBuildingBar
                    onSelect={handleSetBuildingFilter}
                    defaultValue={buildingFilter}
                  />
                </Col>
                <Col>
                  {privilege.edit && (
                    <>
                      <NewProtocolDropdown className="m-3 me-4 float-end" />
                      <Button
                        variant="outline-primary"
                        className="my-3 float-end"
                        onClick={() => setShowLastPage(true)}
                      >
                        Ostatnia strona
                      </Button>
                    </>
                  )}
                </Col>
              </Row>
              <LastPageModal
                show={showLastPage}
                onHide={() => setShowLastPage(false)}
              />
              <OfflineProtocolQueue onEdit={handleEditOfflineProtocol} />
              <FolderGrid folders={folders} onSelect={openFolder} />
            </div>
          }
        />
        <Route
          path="/:buildingId"
          element={<BuildingProtocols onRemove={onRemove} />}
        />
        <Route
          path={"/create/type/:protocolType"}
          element={<CreateProtocol onSave={(p) => handleSave(p, true)} />}
        />
        <Route
          path={"/create/:protocolId"}
          element={<CreateProtocol onSave={(p) => handleSave(p, false)} />}
        />
        <Route
          path={"/offline/:protocolId"}
          element={<CreateProtocol useCachedProtocol={true} />}
        />
        <Route
          path={"/copy/:protocolId"}
          element={
            <CreateProtocol copy={true} onSave={(p) => handleSave(p, true)} />
          }
        />
        <Route
          path={"/create"}
          element={<CreateProtocol onSave={(p) => handleSave(p, true)} />}
        />
      </Routes>
    </>
  );
}

function BuildingProtocols({ onRemove }) {
  const { buildingId } = useParams();
  const { getPrivilege } = usePrivilege();
  const {
    protocols: { get },
  } = useAuth();
  const navigate = useNavigate();
  const [protocols, setProtocols] = useState(undefined);
  const { showAlert, alert } = useAlert();
  const privilege = getPrivilege("protocols");

  useEffect(() => {
    if (protocols !== undefined) return;

    get(({ success, error, content }) => {
      if (!success) return showAlert(error);

      setProtocols(content);
    }, buildingId);
  });

  const handleEdit = (protocolId, copy = false) => {
    const pathPart = copy ? `copy` : `create`;
    const navigationId = protocolId ? `/${protocolId}` : "";
    navigate(`/protocols/${pathPart}${navigationId}`);
  };

  const handleRemoveProtocol = (protocol) => {
    setProtocols((oldProtocols) => {
      const index = oldProtocols.findIndex((p) => p.id === protocol.id);

      if (index === -1) return oldProtocols;

      oldProtocols.splice(index, 1);

      return [...oldProtocols];
    });
    onRemove(protocol);
  };

  const navigateBack = () => {
    navigate(-1);
  };

  return (
    <div className={"container p-4"}>
      {alert()}
      <div className={"container "}>
        <Row>
          <Col>
            <Button
              variant={"outline-secondary"}
              className="m-3 me-4 float-start"
              onClick={navigateBack}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Powrót
            </Button>
          </Col>
          <Col>
            {privilege.edit && (
              <NewProtocolDropdown className="m-3 me-4 float-end" />
            )}
          </Col>
        </Row>
      </div>
      <ProtocolsTable
        protocols={protocols}
        onEdit={handleEdit}
        onDelete={handleRemoveProtocol}
      />
      <Loading show={protocols === undefined} />
    </div>
  );
}
